import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Stethoscope, Plus, Trash2, FileText } from "lucide-react";

const BADGE = {
  REQUESTED: "bg-amber-100 text-amber-900",
  ACCEPTED: "bg-blue-100 text-blue-800",
  REJECTED: "bg-red-100 text-red-700",
  RESCHEDULED: "bg-purple-100 text-purple-800",
  CONFIRMED: "bg-sky-100 text-sky-800",
  IN_PROGRESS: "bg-indigo-100 text-indigo-800",
  COMPLETED: "bg-emerald-100 text-emerald-800",
  CANCELLED: "bg-slate-200 text-slate-600",
  PENDING: "bg-amber-100 text-amber-900",
  VERIFIED: "bg-emerald-100 text-emerald-800",
  PAID: "bg-emerald-100 text-emerald-800",
  DONE: "bg-emerald-100 text-emerald-800",
};

export function VetBadge({ status }) {
  return (
    <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap ${BADGE[status] || "bg-muted"}`}>
      {(status || "").replace(/_/g, " ")}
    </span>
  );
}

export const MODE_LABEL = { online: "Online", clinic: "Clinic Visit", home_visit: "Home Visit", emergency: "EMERGENCY" };
export const vfmt = (v) => `₹${(v || 0).toLocaleString("en-IN")}`;

/** Structured digital prescription viewer (client + vet + admin). */
export function PrescriptionDialog({ rx, open, setOpen }) {
  if (!rx) return null;
  const Row = ({ label, value }) => value ? (
    <div className="flex gap-2 text-sm"><span className="w-28 shrink-0 text-muted-foreground">{label}</span><span className="font-semibold">{value}</span></div>
  ) : null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="rx-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-primary" /> Digital Prescription</DialogTitle>
          <DialogDescription>#{rx.prescription_id?.slice(0, 8)} · {(rx.created_at || "").slice(0, 16).replace("T", " ")}</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-border p-3 space-y-1 bg-muted/40">
          <div className="font-bold">{rx.vet_details?.name} <span className="font-normal text-muted-foreground text-sm">{rx.vet_details?.qualification}</span></div>
          <div className="text-xs text-muted-foreground">
            Reg: {rx.vet_details?.registration_number || "—"} · {rx.vet_details?.clinic_name || ""} · {rx.vet_details?.mobile}
          </div>
        </div>
        <div className="space-y-1.5">
          <Row label="Owner" value={rx.owner_name} />
          <Row label="Animal" value={[rx.animal_info?.name, rx.animal_info?.type, rx.animal_info?.breed, rx.animal_info?.age ? `${rx.animal_info.age} yrs` : ""].filter(Boolean).join(" · ")} />
          <Row label="Diagnosis" value={rx.diagnosis} />
          <Row label="Vaccination" value={rx.vaccination} />
        </div>
        {(rx.medicines || []).length > 0 && (
          <div className="rounded-xl border border-border overflow-hidden">
            <table className="w-full text-xs">
              <thead><tr className="bg-muted text-left"><th className="px-2 py-1.5">Medicine</th><th className="px-2 py-1.5">Dosage</th><th className="px-2 py-1.5">Frequency</th><th className="px-2 py-1.5">Duration</th></tr></thead>
              <tbody>
                {rx.medicines.map((m, i) => (
                  <tr key={i} className="border-t"><td className="px-2 py-1.5 font-bold">{m.name}</td><td className="px-2 py-1.5">{m.dosage}</td><td className="px-2 py-1.5">{m.frequency}</td><td className="px-2 py-1.5">{m.duration}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="space-y-1.5">
          {rx.instructions && <Row label="Advice" value={rx.instructions} />}
          {rx.follow_up_date && <Row label="Follow-up" value={rx.follow_up_date} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Consultation form — vet completes an appointment; creates Rx + payment + follow-up. */
export function ConsultationDialog({ appt, open, setOpen, onDone }) {
  const [f, setF] = useState({ medicines: [] });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setMed = (i, k, v) => setF((p) => {
    const meds = [...p.medicines];
    meds[i] = { ...meds[i], [k]: v };
    return { ...p, medicines: meds };
  });
  const save = async () => {
    if (!f.diagnosis?.trim()) { toast.error("Diagnosis is required"); return; }
    setBusy(true);
    try {
      await api.post("/vet/me/consultations", {
        appointment_id: appt.appointment_id,
        symptoms: f.symptoms || appt.symptoms, observations: f.observations || "",
        diagnosis: f.diagnosis, treatment: f.treatment || "",
        medicines: (f.medicines || []).filter((m) => m.name?.trim()),
        vaccination: f.vaccination || "", advice: f.advice || "",
        follow_up_date: f.follow_up_date || "", notes: f.notes || "",
      });
      toast.success("Consultation completed — prescription generated");
      setOpen(false); setF({ medicines: [] }); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  if (!appt) return null;
  const T = ({ k, label, rows = 2 }) => (
    <div>
      <Label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{label}</Label>
      <Textarea rows={rows} className="am-input mt-1" data-testid={`cons-${k}`} value={f[k] || ""} onChange={(e) => set(k, e.target.value)} />
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="cons-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Stethoscope className="w-5 h-5 text-primary" /> Consultation</DialogTitle>
          <DialogDescription>
            {appt.client_name} · {[appt.animal_info?.type, appt.animal_info?.breed].filter(Boolean).join(" ")} · {MODE_LABEL[appt.mode]}
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm rounded-xl bg-muted p-3"><b>Reported symptoms:</b> {appt.symptoms}</p>
        <T k="observations" label="Clinical Observations" />
        <T k="diagnosis" label="Diagnosis *" />
        <T k="treatment" label="Treatment" />
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Medicines</Label>
            <Button size="sm" variant="outline" className="rounded-full text-xs" data-testid="cons-add-med"
              onClick={() => set("medicines", [...(f.medicines || []), { name: "", dosage: "", frequency: "", duration: "" }])}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add
            </Button>
          </div>
          {(f.medicines || []).map((m, i) => (
            <div key={i} className="grid grid-cols-9 gap-1.5 mt-2 items-center">
              <Input className="am-input col-span-3 h-9 text-xs" placeholder="Medicine" data-testid={`cons-med-name-${i}`} value={m.name} onChange={(e) => setMed(i, "name", e.target.value)} />
              <Input className="am-input col-span-2 h-9 text-xs" placeholder="Dosage" value={m.dosage} onChange={(e) => setMed(i, "dosage", e.target.value)} />
              <Input className="am-input col-span-2 h-9 text-xs" placeholder="Frequency" value={m.frequency} onChange={(e) => setMed(i, "frequency", e.target.value)} />
              <Input className="am-input col-span-1 h-9 text-xs" placeholder="Days" value={m.duration} onChange={(e) => setMed(i, "duration", e.target.value)} />
              <button className="text-destructive" onClick={() => set("medicines", f.medicines.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Vaccination</Label>
            <Input className="am-input mt-1" data-testid="cons-vaccination" value={f.vaccination || ""} onChange={(e) => set("vaccination", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Follow-up Date</Label>
            <Input type="date" className="am-input mt-1" data-testid="cons-followup" value={f.follow_up_date || ""} onChange={(e) => set("follow_up_date", e.target.value)} />
          </div>
        </div>
        <T k="advice" label="Advice / Instructions" />
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="am-btn-primary" disabled={busy} data-testid="cons-save" onClick={save}>Complete & Generate Prescription</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
